import React, { useState, useMemo } from 'react'
import {
  Box,
  TextField,
  FormHelperText,
  Button,
  Typography,
  CircularProgress,
  Autocomplete,
  Dialog,
  DialogContent
} from '@mui/material'
import { useForm, Controller } from 'react-hook-form'
import styled from 'styled-components'
import { STATE_OPTIONS } from '../../constants/state'
import { usePostApplication } from '../../api/application'
import { useSnackbar } from 'notistack'
import { useNavigate } from 'react-router-dom'
import UploadIcon from '@mui/icons-material/Upload'
import { RedeemCounter } from '../../components/RedeemCounter'
import { product } from '../../constants/product'
import { workshop } from '../../constants/workshop'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { LIMIT_USER_REDEMPTION, START_DATE, END_DATE, LIMIT_APPLICATION_BOTTLE } from '../../constants/limit'
import { useGetCount } from '../../api/application'

const StyledFieldBox = styled(Box)`
  && {
    max-width: 800px;
    justify-content: space-evenly;
    min-height: 80px;
    margin: auto;
    display: flex;
    flex-direction: column;
  }
`

export const ApplicationForm = () => {
  const { register, handleSubmit, control, setValue } = useForm({ mode: 'onChange' })
  const { mutateAsync, isLoading } = usePostApplication()
  const { enqueueSnackbar } = useSnackbar()
  const { data } = useGetCount()

  const count = useMemo(() => (data ? data : 0), [data])

  const [uploadError, setUploadError] = useState('')

  const [openDialog, setopenDialog] = useState(false)

  const now = new Date()
  const start = new Date(START_DATE)
  const end = new Date(END_DATE)

  const isOpen = now >= start && now <= end

  const navigate = useNavigate()

  const onSubmit = async (data) => {
    const formData = new FormData()
    formData.append('name', data.name)
    formData.append('phone', data.phone)
    formData.append('state', data.state)
    formData.append('carPlateNumber', data.carPlateNumber)
    formData.append('product', data.product)
    formData.append('quantity', data.quantity)
    formData.append('workshop', data.workshop)
    formData.append('invoiceNumber', data.invoiceNumber)
    formData.append('invoiceImg', data?.invoiceImg?.[0])
    // formData.append('plateNumImg', data?.plateNumImg?.[0])

    const res = await mutateAsync(formData).catch((err) => {
      enqueueSnackbar(err?.response?.data?.message ?? 'Submit fail', { variant: 'error' })
    })

    if (res?.status !== 200) return

    setopenDialog(true)
    setTimeout(() => navigate('/'), 3000)
  }

  const handleClose = () => {
    setopenDialog(false)
  }

  return (
    <Box sx={{ m: { xs: 5, sm: 10 } }}>
      <Box display='flex' justifyContent='flex-end'>
        <Button component='a' href='/' variant='outlined' size='small'>
          Back
        </Button>
      </Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <StyledFieldBox>
          <Controller
            name='name'
            control={control}
            defaultValue=''
            rules={{
              required: 'Name required',
              maxLength: { value: 35, message: 'Exceed word count' }
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                label='Name'
                fullWidth
                variant='standard'
                value={value}
                onChange={onChange}
                error={!!error}
                helperText={error ? error.message : null}
              />
            )}
          />
        </StyledFieldBox>
        <StyledFieldBox>
          <Controller
            name='phone'
            control={control}
            defaultValue=''
            rules={{
              required: 'Phone number required',
              pattern: {
                value: /^(601)[0-46-9]-*[0-9]{7,8}$/,
                message: `Invalid phone number. Phone number should follow the format of 601xxxxxxxx. '+' sign is not required.`
              }
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                label='Phone number'
                variant='standard'
                value={value}
                fullWidth
                onChange={onChange}
                error={!!error}
                helperText={error ? error.message : null}
                placeholder='60127123456'
              />
            )}
          />
        </StyledFieldBox>
        <StyledFieldBox>
          <Controller
            name='state'
            control={control}
            rules={{
              required: 'State is required'
            }}
            render={({ field: { onChange, ...props }, fieldState: { error } }) => (
              <Autocomplete
                options={STATE_OPTIONS}
                onChange={(e, data) => onChange(data)}
                {...props}
                renderInput={(params) => (
                  <TextField
                    variant='standard'
                    error={!!error}
                    fullWidth
                    helperText={error ? error.message : null}
                    {...params}
                    label='State'
                  />
                )}
              />
            )}
          />
        </StyledFieldBox>
        {/* <StyledFieldBox>
          <Controller
            name='email'
            control={control}
            defaultValue=''
            rules={{
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address'
              },
              maxLength: { value: 50, message: 'Exceed word count' }
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                label='Email (Optional)'
                variant='standard'
                value={value}
                fullWidth
                onChange={onChange}
                error={!!error}
                helperText={error ? error.message : null}
              />
            )}
          />
        </StyledFieldBox>
        <StyledFieldBox>
          <Controller
            name='carBrand'
            control={control}
            defaultValue=''
            rules={{
              required: 'Car brand is required',
              maxLength: { value: 25, message: 'Exceed word count' }
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                label='Car Brand (eg.Toyota)'
                variant='standard'
                value={value}
                onChange={onChange}
                error={!!error}
                fullWidth
                helperText={error ? error.message : null}
              />
            )}
          />
        </StyledFieldBox>
        <StyledFieldBox>
          <Controller
            name='carModel'
            control={control}
            defaultValue=''
            rules={{
              required: 'Car Model is required',
              maxLength: { value: 25, message: 'Exceed word count' }
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                label='Car Model (eg. Vios)'
                variant='standard'
                value={value}
                onChange={onChange}
                error={!!error}
                fullWidth
                helperText={error ? error.message : null}
              />
            )}
          />
        </StyledFieldBox> */}
        <StyledFieldBox>
          <Controller
            name='carPlateNumber'
            control={control}
            defaultValue=''
            rules={{
              required: 'Car plate number is required',
              maxLength: { value: 10, message: 'Exceed word count' }
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                label='Car Plate Number'
                variant='standard'
                value={value}
                onChange={onChange}
                error={!!error}
                fullWidth
                helperText={error ? error.message : null}
              />
            )}
          />
        </StyledFieldBox>
        <StyledFieldBox>
          <Controller
            name='product'
            control={control}
            rules={{
              required: 'Product is required'
            }}
            render={({ field: { onChange, ...props }, fieldState: { error } }) => (
              <Autocomplete
                options={product}
                onChange={(e, data) => onChange(data)}
                {...props}
                renderInput={(params) => (
                  <TextField
                    variant='standard'
                    error={!!error}
                    fullWidth
                    helperText={error ? error.message : null}
                    {...params}
                    label='Product'
                  />
                )}
              />
            )}
          />
        </StyledFieldBox>
        <StyledFieldBox>
          <Controller
            name='quantity'
            control={control}
            defaultValue=''
            rules={{
              required: 'Quantity is required',
              min: { value: 1, message: 'Must be more than 0' },
              max: { value: LIMIT_USER_REDEMPTION, message: 'Maximum quantity is 2' }
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                label='Quantity'
                variant='standard'
                value={value}
                onChange={onChange}
                error={!!error}
                fullWidth
                helperText={error ? error.message : null}
                type='number'
              />
            )}
          />
        </StyledFieldBox>
        <StyledFieldBox>
          <Controller
            name='workshop'
            control={control}
            rules={{
              required: 'Workshop is required'
            }}
            render={({ field: { onChange, ...props }, fieldState: { error } }) => (
              <Autocomplete
                options={workshop}
                onChange={(e, data) => onChange(data)}
                {...props}
                renderInput={(params) => (
                  <TextField
                    variant='standard'
                    error={!!error}
                    fullWidth
                    helperText={error ? error.message : null}
                    {...params}
                    label='Workshop'
                  />
                )}
              />
            )}
          />
        </StyledFieldBox>
        <StyledFieldBox>
          <Controller
            name='invoiceNumber'
            control={control}
            defaultValue=''
            rules={{
              required: 'Invoice number is required',
              maxLength: { value: 35, message: 'Exceed word count' }
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              return (
                <TextField
                  label='Invoice Number'
                  variant='standard'
                  value={value}
                  onChange={onChange}
                  error={!!error}
                  fullWidth
                  helperText={error ? error.message : null}
                />
              )
            }}
          />
        </StyledFieldBox>
        <StyledFieldBox display='flex' flexDirection='column'>
          <Box>
            <Controller
              name='invoiceImg'
              control={control}
              defaultValue=''
              rules={{
                required: 'Required'
              }}
              render={({ field: { value }, fieldState: { error } }) => {
                const props = register('invoiceImg')
                const combinedOnchange = (e) => {
                  const fileSize = e?.target?.files?.[0]?.size
                  const fileMb = fileSize / 1024 ** 2
                  if (fileMb > 5) {
                    setUploadError('File size must be within 5MB.')
                    setValue('invoiceImg', null)
                  } else {
                    props.onChange(e)
                    setUploadError('')
                  }
                }
                return (
                  <>
                    <Button variant='outlined' startIcon={<UploadIcon />} component='label'>
                      Upload Receipt/Invoice
                      <input
                        type='file'
                        accept='image/*'
                        hidden
                        {...props}
                        onChange={combinedOnchange}
                      />
                    </Button>
                    {error ? <FormHelperText error={true}>{error?.message}</FormHelperText> : null}
                    {value ? <FormHelperText>{value[0]?.name}</FormHelperText> : null}
                    {/* throw error when file size is >2MB */}
                    {uploadError ? (
                      <FormHelperText error={true}>{uploadError}</FormHelperText>
                    ) : null}
                  </>
                )
              }}
            />
          </Box>
        </StyledFieldBox>
        {/*
        <StyledFieldBox display='flex' flexDirection='column'>
          <Box>
            <Controller
              name='plateNumImg'
              control={control}
              defaultValue=''
              rules={{
                required: 'Required'
              }}
              render={({ field: { value }, fieldState: { error } }) => {
                const props = register('plateNumImg')
                const combinedOnchange = (e) => {
                  const fileSize = e?.target?.files?.[0]?.size
                  const fileMb = fileSize / 1024 ** 2
                  if (fileMb > 5) {
                    setUploadError('File size must be within 5MB.')
                    setValue('plateNumImg', null)
                  } else {
                    props.onChange(e)
                    setUploadError('')
                  }
                }
                return (
                  <>
                    <Button variant='outlined' startIcon={<UploadIcon />} component='label'>
                      Upload Car Plate Number
                      <input
                        type='file'
                        accept='image/*'
                        hidden
                        {...props}
                        onChange={combinedOnchange}
                      />
                    </Button>
                    {error ? <FormHelperText error={true}>{error?.message}</FormHelperText> : null}
                    {value ? <FormHelperText>{value[0]?.name}</FormHelperText> : null}
                    {/* throw error when file size is >2MB *\/}
                    {uploadError ? (
                      <FormHelperText error={true}>{uploadError}</FormHelperText>
                    ) : null}
                  </>
                )
              }}
            />
          </Box>
        </StyledFieldBox>
        */}
        <StyledFieldBox>
          <Box my={2}>
            <Typography variant='h4'>
              *Please make sure your phone number is the same as your Touch’n Go e-Wallet account.
            </Typography>
            <Typography variant='h4'>
              *By submitting this form, you confirm that you have read, understood, and will respect
              the{' '}
              <a
                style={{ color: '#002984' }}
                href={'client-static/documents/Terms and Conditions.pdf'}
                target='_blank'
                rel='noopener noreferrer'
                newTab
              >
                terms and conditions
              </a>{' '}
              &{' '}
              <a
                style={{ color: '#002984' }}
                href={'client-static/documents/Personal Data and Privacy Notice.pdf'}
                rel='noopener noreferrer'
                newTab
              >
                PDPA.
              </a>
            </Typography>
          </Box>
          {/* <RedeemCounter /> */}
          {isOpen && count < LIMIT_APPLICATION_BOTTLE && (
            <Box>
              <Button
                type='submit'
                variant='contained'
                disabled={isLoading}
              >
                {isLoading && <CircularProgress style={{ color: '#fff' }} size='1.5rem' />}
                Submit
              </Button>
            </Box>
          )}
        </StyledFieldBox>
      </form>
      <Dialog open={openDialog} onClose={handleClose}>
        <DialogContent dividers>
          <Box display='flex' justifyContent='flex-start' alignItems='center' pb={3}>
            <CheckCircleIcon style={{ color: '#4caf50' }} />
            <Typography variant={'h3'}>{'Application sent.'}</Typography>
          </Box>
          <Typography gutterBottom>Redirecting back to homepage...</Typography>
        </DialogContent>
      </Dialog>
    </Box>
  )
}
